import { ChangeEvent, Fragment, useEffect, useState } from 'react';

import fields from './fields.json';
import ContentApi from '../../api/contentApi';
import IconButtonLink from '../../components/atoms/anchor/IconButtonLink';
import IconButton from '../../components/atoms/button/IconButton';
import ContentTypeIcon from '../../components/atoms/content-type-icon/ContentTypeIcon';
import Input from '../../components/atoms/input';
import Card from '../../components/organisms/Card';
import { ContentData, ContentType } from '../../types/contentTypes';
import SectionData from '../../types/SectionData';
import SkillData from '../../types/skillData';

import './SectionCard.scss';

interface SectionCardProps {
  section: SectionData;
  skill: SkillData;
  onUpdate: (section: SectionData) => void;
  onDelete: () => void;
  className?: string;
}

export default function SectionCard({ section, skill, onUpdate, onDelete, className = '' }: SectionCardProps) {
  const [name, setName] = useState<string>(section.name);
  const [timerId, setTimerId] = useState<NodeJS.Timeout>();
  const [saving, setSaving] = useState<boolean>(false);
  const [contents, setContents] = useState<ContentData[]>();

  useEffect(() => {
    async function fetchData() {
      const fetchedContents = await ContentApi.getContentsBySectionId(section.id);
      setContents(fetchedContents);
    }

    fetchData();
  }, [section]);

  function handleChange({ target: { value: newName } }: ChangeEvent<HTMLInputElement>) {
    setName(newName);
    clearTimeout(timerId);
    setSaving(true);
    const newTimerId = setTimeout(async () => {
      await onUpdate({ ...section, name: newName });
      setSaving(false);
    }, 1000);
    setTimerId(newTimerId);
  }

  const ContentHeadlines = contents?.map(content => (
    <Fragment key={content.id}>
      <p>{content.headline}</p>
      <ContentTypeIcon type={content.type} />
    </Fragment>
  ));

  const contentTypes = Object.values(ContentType);

  const ContentCounts = contentTypes.map(contentType => {
    if (!contents) return undefined;
    const count = contents.filter(content => content.type === contentType).length;
    return (
      count !== 0 && (
        <span key={contentType} className="flex items-center mr-4">
          <ContentTypeIcon type={contentType} />
          {count > 1 && <span>x{count}</span>}
        </span>
      )
    );
  });

  return (
    <Card className={`section-card ${className} p-16`}>
      <form>
        <label className="secondary-heading block" htmlFor={fields.name.id}>
          Section name
        </label>
        <div className="mb-2">
          <Input className="w-full" {...fields.name} onChange={handleChange} value={name} />
          {saving && <p>Saving...</p>}
        </div>
        <div className="flex ml-3">{ContentCounts}</div>
      </form>
      <div>
        <p className="secondary-heading block">Content headline</p>
        {contents?.length === 0 && (
          <p className="mb-6">There are no contents in this section, go ahead and add some.</p>
        )}
        <div className="content-headlines-container mb-6">{ContentHeadlines}</div>
      </div>
      <IconButton
        className="w-fit"
        visualType="secondary"
        icon="delete-outline"
        label="Delete"
        type="button"
        onClick={onDelete}
      />
      <IconButtonLink
        to={`/skills/${skill.slug}/sections/${section.slug}/contents/edit`}
        icon={`${contents?.length === 0 ? 'add' : 'edit-outline'}`}
        label={`${contents?.length === 0 ? 'Add content' : 'Manage content'}`}
      />
    </Card>
  );
}
